import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import Textarea from '../components/Textarea';

const TextCompare: React.FC = () => {
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');

  const getDiff = () => {
    if (!text1 || !text2) return null;

    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');

    const diff = lines1.map((line, index) => {
      const line2 = lines2[index];
      if (line === line2) {
        return { type: 'same', text: line };
      } else if (!line2) {
        return { type: 'removed', text: line };
      } else if (!line) {
        return { type: 'added', text: line2 };
      } else {
        return { type: 'changed', text: line, text2: line2 };
      }
    });

    // 추가로 text2에만 있는 줄들
    if (lines2.length > lines1.length) {
      for (let i = lines1.length; i < lines2.length; i++) {
        diff.push({ type: 'added', text: lines2[i] });
      }
    }

    return diff;
  };

  const diff = getDiff();
  const totalLines = Math.max(
    text1.split('\n').length,
    text2.split('\n').length
  );
  const sameLines = diff?.filter((d) => d.type === 'same').length || 0;
  const similarity =
    totalLines > 0 ? Math.round((sameLines / totalLines) * 100) : 100;

  return (
    <Layout showBackButton>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-32"
        >
          <h1 className="text-4xl font-bold text-textPrimary mb-8">
            텍스트 비교
          </h1>
          <p className="text-textSecondary">
            두 텍스트의 차이점을 확인하세요
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-24 mb-24"
        >
          <div>
            <label className="block text-textSecondary mb-8 text-sm font-semibold">
              원본 텍스트
            </label>
            <Textarea
              value={text1}
              onChange={setText1}
              placeholder="원본 텍스트를 입력하세요..."
            />
          </div>
          <div>
            <label className="block text-textSecondary mb-8 text-sm font-semibold">
              비교 텍스트
            </label>
            <Textarea
              value={text2}
              onChange={setText2}
              placeholder="비교할 텍스트를 입력하세요..."
            />
          </div>
        </motion.div>

        {text1 && text2 && diff && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-24"
          >
            <div className="bg-surface rounded-large p-24 mb-24">
              <div className="text-center">
                <div className="text-textSecondary text-sm mb-4">동일률</div>
                <div className="text-4xl font-bold text-primary">
                  {similarity}%
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-large p-24">
              <h3 className="text-lg font-bold text-textPrimary mb-16">
                차이점
              </h3>
              <div className="space-y-4">
                {diff.map((item, index) => (
                  <div
                    key={index}
                    className={`p-12 rounded-medium ${
                      item.type === 'same'
                        ? 'bg-gray-50'
                        : item.type === 'added'
                        ? 'bg-green-50'
                        : item.type === 'removed'
                        ? 'bg-red-50'
                        : 'bg-yellow-50'
                    }`}
                  >
                    <div className="flex gap-8 items-start">
                      <span className="text-textSecondary text-sm">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        {item.type === 'changed' ? (
                          <>
                            <div className="text-red-600 line-through">
                              {item.text}
                            </div>
                            <div className="text-green-600">{item.text2}</div>
                          </>
                        ) : (
                          <div
                            className={
                              item.type === 'added'
                                ? 'text-green-600'
                                : item.type === 'removed'
                                ? 'text-red-600'
                                : 'text-textPrimary'
                            }
                          >
                            {item.text}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-textSecondary">
                        {item.type === 'added' && '추가됨'}
                        {item.type === 'removed' && '삭제됨'}
                        {item.type === 'changed' && '변경됨'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default TextCompare;
